import React, { FC, useCallback } from 'react';
import { Avatar, Box, Button, Container, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import ReplayCircleFilledIcon from '@mui/icons-material/ReplayCircleFilled';
import MarkEmailReadIcon from '@mui/icons-material/MarkEmailRead';
import { useMutation } from 'react-query';
import { green, red } from '@mui/material/colors';
import { AxiosError } from 'axios';
import { useAppDispatch } from '../../hooks/redux';
import useUserMeQuery from '../../reactQuery/hooks/useUserMeQuery';
import {
	setForgotPasswordModalOpen,
	setLoginModalOpen,
} from '../../redux/reducers/ui/modalsReducer';

import SimpleModal from '../Modals/SimpleModal';
import Form from '../Form/Form';
import { forgotPasswordFields, SUBMIT_BTN_TYPE } from '../Form/form.utils';
import { FormField } from '../../lib/interfaces/Form';
import { forgotPassword } from '../../api/apis';
import { UserForgotPasswordDTO } from '../../lib/interfaces/User';

const ForgotPasswordModal: FC = () => {
	const dispatch = useAppDispatch();
	const { t } = useTranslation();
	const { data: currentUser } = useUserMeQuery();
	const forgotPasswordFormFields = forgotPasswordFields.map(
		(field: FormField) => {
			if (field.label) {
				field.label = t(field.label);
			}
			return field;
		},
	);

	const forgotPasswordMutation = useMutation<any, AxiosError<any>, string>(
		(requestedEmail: any) => forgotPassword(requestedEmail),
	);

	const handleClose = useCallback(() => {
		if (!currentUser && !forgotPasswordMutation.isSuccess) {
			dispatch(setLoginModalOpen(true));
		}
		dispatch(setForgotPasswordModalOpen(false));
	}, [dispatch, forgotPasswordMutation.isSuccess]);

	const handleSubmit = (formValuesFP: UserForgotPasswordDTO) => {
		forgotPasswordMutation.mutate(formValuesFP.email);
	};

	const content: React.ReactElement = (
		<Container component="main" maxWidth="xs">
			<div className="AuthModal__wrapper">
				<div className="AuthModal__title">
					<Avatar className="AuthModal__avatar">
						<ReplayCircleFilledIcon />
					</Avatar>
					<Typography component="h1" variant="h5">
						{t('passwordRecovery')}
					</Typography>
					<Typography sx={{ textAlign: 'center' }}>
						{t('enterEmailForgotPassword')}
					</Typography>
					{forgotPasswordMutation.isError && (
						<Typography color={red[500]}>
							{forgotPasswordMutation.error?.response?.data?.message}
						</Typography>
					)}
				</div>
				<Form
					fields={forgotPasswordFormFields}
					onSubmit={handleSubmit}
					saveButtonName={t('send')}
					isLoading={forgotPasswordMutation.isLoading}
					saveButtonStyle={SUBMIT_BTN_TYPE.FULL}
					fullWidth
				/>
			</div>
		</Container>
	);

	const successContent: React.ReactElement = (
		<Container component="main" maxWidth="xs">
			<div className="AuthModal__wrapper">
				<div className="AuthModal__title">
					<Avatar color={green[500]} className="AuthModal__avatar success">
						<MarkEmailReadIcon />
					</Avatar>
					<Typography component="h1" variant="h5">
						{t('emailHasBeenSent')}
					</Typography>
					<Typography sx={{ textAlign: 'center' }}>
						{t('emailForgotPasswordInstruction')}
					</Typography>
				</div>
				<Box>
					<Button disableElevation variant="contained" onClick={handleClose}>
						{t('close')}
					</Button>
				</Box>
			</div>
		</Container>
	);

	return (
		<SimpleModal
			handleClose={handleClose}
			content={forgotPasswordMutation.isSuccess ? successContent : content}
			hideActions
		/>
	);
};

export default ForgotPasswordModal;
